import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

type StatusToneKey = 'neutral' | 'info' | 'error' | 'success' | 'closed' | 'archive';

export type StatusBadgeStatus =
  | 'active'
  | 'activeFeminine'
  | 'archive'
  | 'archived'
  | 'closed'
  | 'draft'
  | 'inWork'
  | 'inactive'
  | 'passwordResetRequired'
  | 'processed'
  | 'return'
  | 'system';

// WHY: bare keys, not namespaced — useTranslation('common') below binds the
// namespace and the typed t() expects keys relative to it.
const STATUS_MAP = {
  active: { tone: 'success', labelKey: 'status.active' },
  activeFeminine: { tone: 'success', labelKey: 'status.activeFeminine' },
  archive: { tone: 'archive', labelKey: 'status.archive' },
  archived: { tone: 'archive', labelKey: 'status.archived' },
  closed: { tone: 'closed', labelKey: 'status.closed' },
  draft: { tone: 'neutral', labelKey: 'status.draft' },
  inWork: { tone: 'info', labelKey: 'status.inWork' },
  inactive: { tone: 'neutral', labelKey: 'status.inactive' },
  passwordResetRequired: { tone: 'info', labelKey: 'status.passwordResetRequired' },
  processed: { tone: 'success', labelKey: 'status.processed' },
  return: { tone: 'error', labelKey: 'status.return' },
  system: { tone: 'info', labelKey: 'status.system' },
} as const satisfies Record<StatusBadgeStatus, { tone: StatusToneKey; labelKey: string }>;

const STYLES: Record<StatusToneKey, CSSProperties> = {
  neutral: { background: 'var(--status-neutral-bg)', color: 'var(--status-neutral-fg)' },
  info: { background: 'var(--status-info-bg)', color: 'var(--status-info-fg)' },
  error: { background: 'var(--status-error-bg)', color: 'var(--status-error-fg)' },
  success: { background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' },
  closed: { background: 'transparent', color: 'var(--fg-1)', border: '1px solid var(--border-1)' },
  archive: { background: 'var(--status-archive-bg)', color: 'var(--status-archive-fg)' },
};

export interface StatusBadgeProps {
  status: StatusBadgeStatus | string;
  size?: 'sm' | 'md';
  label?: string;
}

export function StatusBadge({ status, size = 'md', label: labelOverride }: StatusBadgeProps) {
  const { t } = useTranslation('common');
  const m = STATUS_MAP[status as StatusBadgeStatus] ?? null;
  const tone = m?.tone ?? 'neutral';
  const label = labelOverride ?? (m ? t(m.labelKey) : status);
  const h = size === 'sm' ? 20 : 22;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: h,
        padding: `0 ${size === 'sm' ? 7 : 9}px`,
        borderRadius: 'var(--r-1)',
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        ...STYLES[tone],
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {label}
    </span>
  );
}
